import { Link } from "react-router-dom";
import { ArrowUp, Mail, MapPin, Phone } from "lucide-react";
import { Logo } from "./Logo";
import { COMPANY, NAV } from "./data";
import { goTo } from "./scroll";
import { WhatsAppIcon } from "./icons";
import shared from "@/seo/shared";

const MAPS = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${COMPANY.street}, ${COMPANY.city}`)}`;
const SOCIAL = [
  { id: "whatsapp", label: "WhatsApp", href: COMPANY.whatsapp, icon: <WhatsAppIcon className="h-5 w-5" />, ext: true },
  { id: "phone", label: "Anrufen", href: COMPANY.phoneHref, icon: <Phone size={18} /> },
  { id: "email", label: "E-Mail schreiben", href: `mailto:${COMPANY.email}`, icon: <Mail size={18} /> },
  { id: "maps", label: "Route in Google Maps", href: MAPS, icon: <MapPin size={18} />, ext: true },
];
const toTop = (e) => {
  e.preventDefault();
  if (window.__lenis) window.__lenis.scrollTo(0, { duration: 1.4 });
  else window.scrollTo({ top: 0, behavior: "smooth" });
};

const heading = "text-xs font-semibold uppercase tracking-[0.24em] text-terra";
const link = "text-slate-300 transition-colors hover:text-white";

export const Footer = ({ onCookies }) => (
  <footer className="relative overflow-hidden bg-[#070A10] pb-20 pt-20 sm:pt-28 lg:pb-0" data-testid="site-footer">
    <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12">
      <div className="grid gap-12 sm:grid-cols-2 lg:grid-cols-12">
        <div className="lg:col-span-3">
          <Logo />
          <p className="mt-6 max-w-sm text-sm leading-relaxed text-slate-300">Ihr Meisterbetrieb für Dacheindeckung, Dachsanierung und Spenglerarbeiten in Schifferstadt, Speyer und der Rhein-Neckar-Region – seit {COMPANY.founded}.</p>
          <div className="mt-6 flex gap-3">
            {SOCIAL.map((s) => (
              <a key={s.id} href={s.href} aria-label={s.label} title={s.label} {...(s.ext ? { target: "_blank", rel: "noopener noreferrer" } : {})} className="grid h-12 w-12 place-items-center rounded-full border border-white/15 text-white transition-[background-color,border-color,transform] duration-300 hover:-translate-y-1 hover:border-terra hover:bg-terra-deep" data-testid={`footer-social-${s.id}`}>{s.icon}</a>
            ))}
          </div>
        </div>
        <nav className="lg:col-span-2" aria-label="Footer-Navigation">
          <p className={heading}>Navigation</p>
          <ul className="mt-5 space-y-3 text-sm">{NAV.map((n) => <li key={n.id}><a href={`/#${n.id}`} onClick={goTo(n.id)} className={link} data-testid={`footer-link-${n.id}`}>{n.label}</a></li>)}</ul>
        </nav>
        <div className="lg:col-span-2">
          <p className={heading}>Leistungen</p>
          <ul className="mt-5 space-y-3 text-sm">{shared.services.map((s) => <li key={s.slug}><Link to={`/${s.slug}`} className={link} data-testid={`footer-service-${s.sid}`}>{s.name}</Link></li>)}</ul>
        </div>
        <div className="lg:col-span-2">
          <p className={heading}>Einsatzgebiet</p>
          <ul className="mt-5 space-y-3 text-sm">
            {shared.locations.map((l) => <li key={l.slug}><Link to={`/${l.slug}`} className={link}>Dachdecker {l.city}</Link></li>)}
            <li><Link to="/einsatzgebiet" className="font-semibold text-white hover:text-terra" data-testid="footer-area-link">Gesamtes Einsatzgebiet</Link></li>
          </ul>
        </div>
        <div className="lg:col-span-3">
          <p className={heading}>Kontakt</p>
          <ul className="mt-5 space-y-3 text-sm text-slate-300">
            <li><a href={COMPANY.phoneHref} className="font-display text-2xl font-bold text-white hover:text-terra" data-testid="footer-phone">{COMPANY.phoneDisplay}</a></li>
            <li><a href={`mailto:${COMPANY.email}`} className={`${link} break-all`} data-testid="footer-email">{COMPANY.email}</a></li>
            <li>{COMPANY.street}, {COMPANY.city}</li>
            <li>{COMPANY.hours}</li>
            <li>Sturmschaden-Notdienst: 24/7</li>
            <li><Link to="/kontakt" className={link} data-testid="footer-contact-page">Kontaktseite</Link></li>
          </ul>
        </div>
      </div>
      <div className="mt-16 flex flex-col gap-4 border-t border-white/10 py-6 text-xs text-slate-400 sm:flex-row sm:items-center sm:justify-between">
        <p>© {new Date().getFullYear()} {COMPANY.full}. Alle Rechte vorbehalten.</p>
        <div className="flex flex-wrap items-center gap-5">
          <Link to="/impressum" className="font-semibold text-slate-200 hover:text-white" data-testid="footer-impressum">Impressum</Link>
          <Link to="/datenschutz" className="font-semibold text-slate-200 hover:text-white" data-testid="footer-datenschutz">Datenschutz</Link>
          <button type="button" onClick={onCookies} className="font-semibold text-slate-200 hover:text-white" data-testid="footer-cookie-settings">Cookie-Einstellungen</button>
          <a href="#top" onClick={toTop} className="grid h-10 w-10 place-items-center rounded-full border border-white/15 text-white hover:border-terra" aria-label="Nach oben" data-testid="footer-back-to-top"><ArrowUp size={16} /></a>
        </div>
      </div>
    </div>
    <p aria-hidden="true" className="pointer-events-none select-none whitespace-nowrap text-center font-display text-[17vw] font-bold uppercase leading-[0.78] text-outline-faint">Bedachungen</p>
  </footer>
);
